import type React from "react";
import { lazy, Suspense } from "react";

import Icon from "@imminently/decisively-ui/Icon";


import { BuildPage } from "@/pages/Build";
import ReleaseWrapper from "@/pages/models/release/ReleaseWrapper";
import { Roles } from "@/auth/permissions";
import { useTranslation } from "react-i18next";
import LoadingOverlay from "@imminently/decisively-ui/LoadingOverlay";
import type { FeatureSettingPath } from "@/config/features";

// This is the ideal code split flow as it will cache the result of the import
// the parent layout handles the react suspense fallback
const DocumentsView = lazy(() => import("@/pages/documents/FileExplorer/FileExplorer"));
const RuleGraph = lazy(() => import("@/pages/models/release/Graph/ExplorePage"));
const Debugger = lazy(() => import("@/pages/models/release/Debug"));
const Flow = lazy(() => import("@/pages/models/release/Flow"));
const DocumentView = lazy(() => import("@/pages/documents/DocumentView"));
const Entities = lazy(() => import("@/pages/models/release/DataModel/route/Entities"));
const Attributes = lazy(() => import("@/pages/models/release/DataModel/route/Attributes/Attributes"));
const Enumerations = lazy(() => import("@/pages/models/release/DataModel/route/Enumerations"));
const DerivedTables = lazy(() => import("@/pages/models/release/DataModel/route/DerivedTables"));

const InterviewDesigner = lazy(() => import("@/pages/models/release/Interview/Designer/InterviewDesigner"));
const Conversation = lazy(() => import("@/pages/models/release/Conversation"));
const Decision = lazy(() => import("@/pages/models/release/Decision"));
const BotPage = lazy(() => import("@/pages/bots/BotPage"));
const DecisionFlow = lazy(() => import("@/pages/models/release/DecisionFlow"));

const ReleaseApi = lazy(() => import("@/pages/models/release/ReleaseApi"));
const Adapters = lazy(() => import("@/pages/models/release/Technical/Adapters"));
const Mapping = lazy(() => import("@/pages/models/release/Technical/MappingV2"));
const Connections = lazy(() => import("@/pages/models/release/Technical/Connections"));
const Reporting = lazy(() => import("@/pages/models/release/Technical/Reporting"));
const Variables = lazy(() => import("@/pages/models/release/Technical/Variables/VariablesPage"));
const FileProcessing = lazy(() => import("@/pages/models/release/Technical/FileProcessing/FileProcessingPage"));

const RulesGroup = {
  name: "Rules",
  icon: <Icon name="documents" />,
  open: true,
};

const DataModelGroup = {
  name: "Data model",
  icon: <Icon name="implementation" />,
  open: true,
};

const DesignGroup = {
  name: "Designs",
  icon: <Icon name="designer" />,
  open: true,
};

const AboutGroup = {
  name: "Technical",
  icon: <Icon name="settings" />,
  open: true,
};

interface SubrouteProps {
  name: string;
  icon?: React.ReactElement;
  group: any;
  comp: string | React.ReactElement;
  path?: string | string[];
  scopeComponent?: string | React.ReactElement;
  translationKey?: string;
  role?: Roles[];
  feature?: FeatureSettingPath;
}

const LoadingFallback = <LoadingOverlay message="Loading..." />;

const subroute = ({ name, icon, group, comp, path, role, ...props }: SubrouteProps) => ({
  path: path || `/build/${name.toLowerCase().replace(" ", "-")}`,
  component: () => {
    const { t } = useTranslation();
    if (!comp) return <BuildPage name={name} />;
    return (
      <ReleaseWrapper title={props.translationKey ? t(props.translationKey) : name} {...props}>
        {comp}
      </ReleaseWrapper>
    );
  },
  exact: true,
  group,
  sidebar: {
    name,
    translationKey: props.translationKey,
    icon,
  },
  role,
  feature: props.feature,
});

export const build = [
  {
    path: "/doc/:doc",
    role: [Roles.ProjectReader, Roles.ProjectWriter],
    layout: "async",
    component: () => (
      <Suspense fallback={LoadingFallback}>
        <DocumentView />
      </Suspense>
    ),
    exact: true,
  },
  {
    path: ["/build", "/models"],
    layout: "decisively-scoped",
    role: [Roles.ProjectReader, Roles.ProjectWriter, Roles.ProjectTechnical],
    children: [
      // Rules
      subroute({ path: ["/build"], name: "Documents", translationKey: 'documents.title', group: RulesGroup, comp: <DocumentsView />, role: [Roles.ProjectReader, Roles.ProjectWriter] }),
      //subroute({ path: ["/build/legacy"], name: "Documents (legacy)", group: RulesGroup, comp: <Documents />, skeleton: undefined, role: [Roles.ProjectReader, Roles.ProjectWriter] }),
      subroute({ name: "Explore", group: RulesGroup, comp: <RuleGraph />, translationKey: 'build.explore' }),
      // will be moved to full screen modal based on context
      subroute({ name: "Debugger", group: RulesGroup, comp: <Debugger />, translationKey: 'build.debugger', role: [Roles.Decision] }),
      subroute({ name: "Flows", group: RulesGroup, comp: <Flow />, translationKey: 'build.flows' }),

      // Data model
      subroute({ name: "Entities", group: DataModelGroup, comp: <Entities />, translationKey: "build.entities" }),
      subroute({ name: "Attributes", group: DataModelGroup, comp: <Attributes />, translationKey: "build.attributes" }),
      subroute({ name: "Enumerations", group: DataModelGroup, comp: <Enumerations />, translationKey: "build.enumerations" }),
      subroute({ name: "Derived Tables", group: DataModelGroup, comp: <DerivedTables />, translationKey: "build.derived_tables" }),

      // Designers
      {
        path: "/build/design",
        exact: true,
        sidebar: { name: "Interview", translationKey: "build.interview" },
        group: DesignGroup,
        component: () => (
          <Suspense fallback={LoadingFallback}>
            <InterviewDesigner />
          </Suspense>
        ),
      },
      {
        path: "/build/conversation",
        exact: true,
        sidebar: { name: "Conversation", translationKey: "build.conversation" },
        group: DesignGroup,
        feature: "legacy_pages.conversations",
        component: () => (
          <Suspense fallback={LoadingFallback}>
            <Conversation />
          </Suspense>
        ),
      },
      {
        path: "/build/reports",
        exact: true,
        sidebar: { name: "Reports", translationKey: "build.reports" },
        group: DesignGroup,
        feature: "legacy_pages.reports",
        component: () => (
          <Suspense fallback={LoadingFallback}>
            <Decision />
          </Suspense>
        ),
      },
      {
        path: "/build/bots",
        exact: true,
        sidebar: { name: "Bots", translationKey: "build.bots" },
        group: DesignGroup,
        feature: "legacy_pages.bots",
        component: () => (
          <Suspense fallback={LoadingFallback}>
            <BotPage />
          </Suspense>
        ),
        role: [Roles.BotReader, Roles.BotWriter, Roles.ProjectWriter, Roles.ProjectTechnical]
      },
      /*{
        path: "/build/decisionflow",
        exact: true,
        sidebar: { name: "Decision Flow", translationKey: "build.decision_flow" },
        group: DesignGroup,
        component: DecisionFlow,
      },*/
      // subroute({ name: 'Reports', group: DesignGroup, comp: () => null }),

      // About
      subroute({ name: "API", translationKey: "build.api", group: AboutGroup, comp: <ReleaseApi /> }),
      subroute({
        name: "Adapters",
        path: "/build/adapters",
        group: AboutGroup,
        comp: <Adapters />,
      }),
      subroute({ name: "Mapping", translationKey: "build.mapping", group: AboutGroup, comp: <Mapping /> }),
      subroute({ name: "Connections", translationKey: "build.connections", group: AboutGroup, comp: <Connections /> }),
      subroute({ name: "Reporting", translationKey: "build.reporting", group: AboutGroup, comp: <Reporting />, feature: "legacy_pages.reporting" }),
      subroute({ name: "Variables", group: AboutGroup, comp: <Variables /> }),
      subroute({ name: "File Processing", translationKey: "build.file_processing", group: AboutGroup, comp: <FileProcessing /> }),

    ],
  },
];
